import './FunctionalButtonsRow.css';

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Button from '../../Button/Button';
import ArrowIcon from '../../ArrowIcon/ArrowIcon';
import OverflowTriggerPopover, {
  OverflowItem,
} from '../../OverflowTriggerPopover/OverflowTriggerPopover';
import { computeOverflowFit } from '../../../utils/computeOverflowFit';
import { sumWithGaps } from '../../../utils/sumWithGaps';
import useResizeTick from '../../../hooks/useResizeTick';
import { getFunctionalButtonsLayoutKey } from './utils/getFunctionalButtonsLayoutKey';
import { useIsCompact } from './hooks/useIsCompact';
import CompactButtonsRow from './components/CompactButtonsRow';
import FunctionalButton from './components/FunctionalButton';
import FunctionalMirrorButton from './components/FunctionalMirrorButton';

export interface FunctionalButtonConfig {
  key: string;
  label: string;
  icon: string;
  onClick: () => void;
  disabled?: boolean;
  alwaysVisible?: boolean;
  /** Higher priority hides last. Required for hideable buttons. */
  hidePriority?: number;
  className?: string;
  /** Optional DOM id — used to wire an external IonPopover trigger to this button. */
  id?: string;
  /** When the button is hidden into the overflow popover, render these as a nested submenu. */
  overflowChildren?: OverflowItem[];
}

interface FunctionalButtonsRowProps {
  buttons: FunctionalButtonConfig[];
  /** Outer flex container — used to measure remaining slack for sticky-release. */
  headerRowRef: React.RefObject<HTMLElement | null>;
  popoverLabel: string;
  /** Fired whenever the hidden count converges. Parent uses this to lock dataset-buttons-wrapper. */
  onHiddenChange?: (state: { hidden: number; totalHideable: number }) => void;
}

const FunctionalButtonsRow = ({
  buttons,
  headerRowRef,
  popoverLabel,
  onHiddenChange,
}: FunctionalButtonsRowProps) => {
  const [fitState, setFitState] = useState<{
    key: string;
    hidden: number;
    measured: boolean;
  }>({ key: '', hidden: 0, measured: false });
  /** Min-width on functional-row = always-visible widths + trigger + gaps. Forces .header overflow scroll past it. */
  const [lockedWidth, setLockedWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mirrorRef = useRef<HTMLDivElement | null>(null);
  const onHiddenChangeRef = useRef(onHiddenChange);
  onHiddenChangeRef.current = onHiddenChange;

  const triggerId = `functional-trigger-${useId().replace(/:/g, '')}`;

  const hideableButtons = useMemo(
    () =>
      buttons
        .filter((button) => !button.alwaysVisible)
        .sort((a, b) => (b.hidePriority ?? 0) - (a.hidePriority ?? 0)),
    [buttons]
  );
  const alwaysVisibleButtons = useMemo(
    () => buttons.filter((button) => button.alwaysVisible),
    [buttons]
  );

  const layoutKey = useMemo(
    () =>
      getFunctionalButtonsLayoutKey([
        ...hideableButtons,
        ...alwaysVisibleButtons,
      ]),
    [hideableButtons, alwaysVisibleButtons]
  );

  const uiLoading = !fitState.measured || fitState.key !== layoutKey;
  const hiddenCount = fitState.key === layoutKey ? fitState.hidden : 0;
  const visibleHideableCount = Math.max(
    hideableButtons.length - hiddenCount,
    0
  );
  const visibleHideableButtons = hideableButtons.slice(0, visibleHideableCount);
  const hiddenHideableButtons = hideableButtons.slice(visibleHideableCount);

  const resizeTick = useResizeTick(
    [containerRef, mirrorRef, headerRowRef],
    layoutKey
  );

  const isCompact = useIsCompact();

  useEffect(() => {
    if (!isCompact) return;
    onHiddenChangeRef.current?.({
      hidden: 0,
      totalHideable: hideableButtons.length,
    });
  }, [isCompact, hideableButtons.length]);

  useEffect(() => {
    if (!onHiddenChangeRef.current) return;
    if (!fitState.measured) return;
    if (fitState.key !== layoutKey) return;
    onHiddenChangeRef.current({
      hidden: fitState.hidden,
      totalHideable: hideableButtons.length,
    });
  }, [fitState, layoutKey, hideableButtons.length]);

  useEffect(() => {
    if (isCompact) return;
    const containerElement = containerRef.current;
    const mirrorElement = mirrorRef.current;
    if (!containerElement || !mirrorElement) {
      return;
    }

    const containerWidth = containerElement.clientWidth;
    if (containerWidth === 0) return;

    const hideableElements = Array.from(
      mirrorElement.querySelectorAll<HTMLElement>('.functional-mirror-hideable')
    );
    const triggerElement = mirrorElement.querySelector<HTMLElement>(
      '.functional-mirror-trigger'
    );
    const alwaysElements = Array.from(
      mirrorElement.querySelectorAll<HTMLElement>('.functional-mirror-always')
    );

    if (
      hideableElements.length !== hideableButtons.length ||
      alwaysElements.length !== alwaysVisibleButtons.length
    ) {
      return;
    }

    const hideableRects = hideableElements.map((element) =>
      element.getBoundingClientRect()
    );
    const alwaysRects = alwaysElements.map((element) =>
      element.getBoundingClientRect()
    );
    const allWidths = [...hideableRects, ...alwaysRects].map(
      (rect) => rect.width
    );
    if (allWidths.length > 0 && allWidths.some((width) => width === 0)) {
      return;
    }

    // Gap between adjacent mirror siblings — skip pairs around trigger so its width does not inflate it.
    let gap = 0;
    if (alwaysRects.length >= 2) {
      gap = Math.max(0, alwaysRects[1].left - alwaysRects[0].right);
    } else if (hideableRects.length >= 2) {
      gap = Math.max(0, hideableRects[1].left - hideableRects[0].right);
    }

    const triggerWidth = triggerElement
      ? triggerElement.getBoundingClientRect().width
      : 0;

    // Slack in headerRow — manual children sum vs inner width (scrollWidth unreliable when overflow-x is visible).
    const headerEl = headerRowRef.current;
    let headerSlack = 0;
    if (headerEl) {
      const computedStyle = window.getComputedStyle(headerEl);
      const paddingX =
        parseFloat(computedStyle.paddingLeft || '0') +
        parseFloat(computedStyle.paddingRight || '0');
      const innerWidth = headerEl.clientWidth - paddingX;
      const childrenSum = Array.from(headerEl.children).reduce(
        (sum, element) => sum + element.getBoundingClientRect().width,
        0
      );
      headerSlack = innerWidth - childrenSum;
    }

    // Free header space is absorbed by margin-left:auto, pinning the row to its collapsed width; add
    // the slack back so the fit sees the room available to re-expand (clamp keeps collapse unchanged).
    const availableBudget = containerWidth + Math.max(0, headerSlack);

    const { hiddenHideable } = computeOverflowFit({
      hideableWidths: hideableRects.map((rect) => rect.width),
      alwaysVisibleWidths: alwaysRects.map((rect) => rect.width),
      triggerWidth,
      gap,
      budget: availableBudget,
    });

    const computedLockedWidth = sumWithGaps(
      [triggerWidth, ...alwaysRects.map((rect) => rect.width)],
      gap
    );
    setLockedWidth((previous) =>
      previous === computedLockedWidth ? previous : computedLockedWidth
    );

    setFitState((previous) =>
      previous.measured &&
      previous.key === layoutKey &&
      previous.hidden === hiddenHideable
        ? previous
        : { key: layoutKey, hidden: hiddenHideable, measured: true }
    );
  }, [hideableButtons, alwaysVisibleButtons, layoutKey, resizeTick, isCompact]);

  const rowStyle =
    lockedWidth > 0 &&
    hiddenCount === hideableButtons.length &&
    hideableButtons.length > 0
      ? { minWidth: lockedWidth }
      : undefined;

  if (isCompact) {
    return <CompactButtonsRow buttons={buttons} containerRef={containerRef} />;
  }

  return (
    <div className="functional-row" ref={containerRef} style={rowStyle}>
      {/* Off-screen mirror for one-shot fit calculation. */}
      <div className="functional-row-mirror" ref={mirrorRef} aria-hidden="true">
        {hideableButtons.map((button) => (
          <FunctionalMirrorButton
            key={`mirror-${button.key}`}
            button={button}
            mirrorClass="functional-mirror-hideable"
          />
        ))}
        <Button
          className="dataset-additional-buttons-trigger functional-mirror-trigger"
          fill="clear"
          round={false}
        >
          <ArrowIcon direction="down" color="primary" />
          {popoverLabel}
        </Button>
        {alwaysVisibleButtons.map((button) => (
          <FunctionalMirrorButton
            key={`mirror-${button.key}`}
            button={button}
            mirrorClass="functional-mirror-always"
          />
        ))}
      </div>

      <div
        className={clsx(
          'functional-row-visible',
          uiLoading && 'functional-row-loading'
        )}
      >
        {visibleHideableButtons.map((button) => (
          <FunctionalButton key={button.key} button={button} />
        ))}

        {hiddenHideableButtons.length > 0 && (
          <OverflowTriggerPopover
            triggerId={triggerId}
            label={popoverLabel}
            items={hiddenHideableButtons.map((button) => ({
              key: button.key,
              label: button.label,
              icon: button.icon,
              onClick: button.onClick,
              disabled: button.disabled,
              children: button.overflowChildren,
            }))}
          />
        )}

        {alwaysVisibleButtons.map((button) => (
          <FunctionalButton key={button.key} button={button} />
        ))}
      </div>
    </div>
  );
};

export default FunctionalButtonsRow;
